/**
 * cues-alerts.ts — procedural alert tone builders (v2M8.3).
 *
 * Implements AUDIO §10's four alert-category cues. All four route
 * through the UI bus per spec ("All play through the UI bus" —
 * non-positional, not subject to camera attenuation).
 *
 * Tuning targets:
 *   - **Red (combat)**: descending two-tone warning, 400 ms total.
 *     Square-wave-flavoured triangle pulses (880 → 440 Hz) read as
 *     urgent without being shrill.
 *   - **Amber (warning)**: single mid tone, 300 ms. 600 Hz triangle
 *     with a soft attack and gentle decay — calm advisory, not alarm.
 *   - **Blue (diplomatic)**: rising two-tone chime, 350 ms. Two sine
 *     tones (C5 → G5, ≈523 → 784 Hz) with bell-like attack.
 *   - **Green (completion)**: soft ascending bell, 300 ms. Layered
 *     fundamental + 2nd harmonic with a gentle attack and longer
 *     decay than the click — feels rewarding without nagging.
 *
 * Volumes peak around 0.3 of the bus gain so several alerts in quick
 * succession don't clip. Red is slightly louder (0.35) per spec's
 * "higher priority — never ducked" treatment.
 */

import type { AudioEngine } from './engine';

/** Register all four §10 alert cues against the engine. */
export function registerAlertCues(engine: AudioEngine): void {
  engine.registry.registerProcedural('alert:red', 'ui', buildAlertRed);
  engine.registry.registerProcedural('alert:amber', 'ui', buildAlertAmber);
  engine.registry.registerProcedural('alert:blue', 'ui', buildAlertBlue);
  engine.registry.registerProcedural('alert:green', 'ui', buildAlertGreen);
}

function buildAlertRed(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Pulse 1 — primary, returned to the engine.
  const osc1 = ctx.createOscillator();
  osc1.type = 'triangle';
  osc1.frequency.value = 880;
  const gain1 = ctx.createGain();
  gain1.gain.setValueAtTime(0.0001, now);
  gain1.gain.exponentialRampToValueAtTime(0.35, now + 0.01);
  gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  osc1.connect(gain1);
  gain1.connect(target);
  // Returned node — call start() before stop() so recent Chrome doesn't
  // throw. engine.ts's redundant start() is caught defensively.
  osc1.start(now);
  osc1.stop(now + 0.19);
  // Pulse 2 — descending tone, started internally after a 30 ms gap.
  const osc2 = ctx.createOscillator();
  osc2.type = 'triangle';
  osc2.frequency.value = 440;
  const gain2 = ctx.createGain();
  gain2.gain.setValueAtTime(0.0001, now + 0.21);
  gain2.gain.exponentialRampToValueAtTime(0.35, now + 0.22);
  gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
  osc2.connect(gain2);
  gain2.connect(target);
  osc2.start(now + 0.21);
  osc2.stop(now + 0.41);
  return osc1;
}

function buildAlertAmber(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 600;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  // Soft attack (40 ms), short sustain, gentle exponential decay.
  gain.gain.exponentialRampToValueAtTime(0.28, now + 0.04);
  gain.gain.setValueAtTime(0.28, now + 0.18);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
  osc.connect(gain);
  gain.connect(target);
  osc.start(now);
  osc.stop(now + 0.31);
  return osc;
}

function buildAlertBlue(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Tone 1 — C5, primary.
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.value = 523;
  const gain1 = ctx.createGain();
  gain1.gain.setValueAtTime(0.0001, now);
  gain1.gain.exponentialRampToValueAtTime(0.3, now + 0.01);
  gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  osc1.connect(gain1);
  gain1.connect(target);
  osc1.start(now);
  osc1.stop(now + 0.17);
  // Tone 2 — G5, rising.
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.value = 784;
  const gain2 = ctx.createGain();
  gain2.gain.setValueAtTime(0.0001, now + 0.18);
  gain2.gain.exponentialRampToValueAtTime(0.3, now + 0.19);
  gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
  osc2.connect(gain2);
  gain2.connect(target);
  osc2.start(now + 0.18);
  osc2.stop(now + 0.36);
  return osc1;
}

function buildAlertGreen(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Fundamental — ascending sweep 660 → 880 Hz over the first 80 ms,
  // then sustained briefly before tail-out. Returned to the engine.
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(660, now);
  osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.28, now + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
  osc.connect(gain);
  gain.connect(target);
  osc.start(now);
  osc.stop(now + 0.31);
  // 2nd harmonic — softer, sweeps with the fundamental for a bell-like
  // overtone. Started in lockstep with the primary.
  const harmonic = ctx.createOscillator();
  harmonic.type = 'sine';
  harmonic.frequency.setValueAtTime(1320, now);
  harmonic.frequency.exponentialRampToValueAtTime(1760, now + 0.08);
  const harmonicGain = ctx.createGain();
  harmonicGain.gain.setValueAtTime(0.0001, now);
  harmonicGain.gain.exponentialRampToValueAtTime(0.12, now + 0.03);
  harmonicGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  harmonic.connect(harmonicGain);
  harmonicGain.connect(target);
  harmonic.start(now);
  harmonic.stop(now + 0.26);
  return osc;
}
